import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../axios/axiosInstance";
import { useAuth } from "../../../Context/AuthContext";
import "./welcomePopUp.css";

const WORDS = ["WELCOME"];
const WORD_GAP = 0.45;
const LETTER_PITCH = 0.78;
const LETTER_FILLS = ["#f3c94b", "#e8203f", "#ffffff"];
const PALETTE = ["#e8203f", "#f3c94b", "#ffffff", "#ff7aa2", "#5ad1ff", "#8ef58a"];
const ANTICS = ["aWobble", "aJump", "aSpin", "aPeek", "aShrug"];

const rnd = (a, b) => a + Math.random() * (b - a);
const pick = () => PALETTE[(Math.random() * PALETTE.length) | 0];

const letterBotSVG = (ch, fill) =>
  '<svg class="bot" viewBox="0 0 48 80" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<text x="24" y="29" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="33" fill="' +
  fill +
  '" stroke="#140d26" stroke-width="1.4" paint-order="stroke">' +
  ch +
  "</text>" +
  '<rect x="18" y="60" width="5" height="12" rx="2.4" fill="#6c7487"/>' +
  '<rect x="25" y="60" width="5" height="12" rx="2.4" fill="#6c7487"/>' +
  '<path d="M17 50 C 10 45, 9 36, 11 30" stroke="#7b8398" stroke-width="3.6" stroke-linecap="round" fill="none"/>' +
  '<path d="M31 50 C 38 45, 39 36, 37 30" stroke="#7b8398" stroke-width="3.6" stroke-linecap="round" fill="none"/>' +
  '<circle cx="11" cy="29" r="3.2" fill="#ffffff"/><circle cx="37" cy="29" r="3.2" fill="#ffffff"/>' +
  '<rect x="15" y="46" width="18" height="16" rx="5" fill="#878fa1"/>' +
  '<rect x="19" y="52" width="10" height="5" rx="2" fill="#2a2f3a"/>' +
  '<rect x="17" y="32" width="14" height="13" rx="4.5" fill="#878fa1"/>' +
  '<rect x="20" y="36" width="8" height="5.5" rx="2.5" fill="#2a2f3a"/>' +
  '<g class="eyes"><circle cx="22" cy="38.8" r="1.4" fill="#f3c94b"/><circle cx="26" cy="38.8" r="1.4" fill="#f3c94b"/></g>' +
  "</svg>";

const anticAttrs = () => {
  const a = ANTICS[(Math.random() * ANTICS.length) | 0];
  const dur = (3.4 + Math.random() * 1.6).toFixed(1);
  const delay = (Math.random() * 2.4).toFixed(1);
  return 'class="wbot-antic ' + a + '" style="--ad:' + dur + "s;--adl:" + delay + 's"';
};

const lettersOf = (ws) => ws.reduce((n, w) => n + w.length, 0);
const reqUnits = (ws) => LETTER_PITCH * lettersOf(ws) + ws.length * (1 - LETTER_PITCH) + (ws.length - 1) * WORD_GAP + 0.5;

const layout = (phrase, rowWidth) => {
  const words = phrase.split(/\s+/).filter(Boolean);
  const avail = Math.max(190, (rowWidth || 300) - 16);
  const bwOne = avail / reqUnits(words);
  if (words.length <= 1 || bwOne >= 30) {
    return { lines: [words], bw: Math.max(20, Math.min(54, bwOne)) };
  }
  let best = null;
  for (let s = 1; s < words.length; s++) {
    const a = words.slice(0, s);
    const b = words.slice(s);
    const mx = Math.max(reqUnits(a), reqUnits(b));
    if (!best || mx < best.mx) best = { mx, lines: [a, b] };
  }
  return { lines: best.lines, bw: Math.max(18, Math.min(38, avail / best.mx)) };
};

const renderWord = (wordRow, phrase) => {
  if (!wordRow) return;
  const plan = layout(phrase, wordRow.clientWidth);
  let gi = 0;
  let html = "";
  wordRow.style.setProperty("--bw", plan.bw.toFixed(1) + "px");
  plan.lines.forEach((line) => {
    html += '<div class="wline">';
    line.forEach((w, wi) => {
      if (wi > 0) html += '<span class="wgap"></span>';
      for (let ci = 0; ci < w.length; ci++) {
        const fill = LETTER_FILLS[gi % LETTER_FILLS.length];
        html +=
          '<div class="wbot"><span class="wbot-inner" style="--d:' +
          (gi * 0.06).toFixed(2) +
          's"><span ' +
          anticAttrs() +
          ">" +
          letterBotSVG(w.charAt(ci), fill) +
          "</span></span></div>";
        gi++;
      }
    });
    html += "</div>";
  });
  wordRow.innerHTML = html;
};

const startFireworks = (canvas) => {
  const ctx = canvas?.getContext("2d");
  if (!ctx) return () => {};
  const reduceFx = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let run = true;
  let raf = 0;
  let W = 0;
  let H = 0;
  let nextLaunch = 0;
  let confetti = [];
  const rockets = [];
  const sparks = [];
  const flashes = [];

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth;
    H = canvas.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  const seedConfetti = () => {
    confetti = [];
    for (let i = 0; i < 32; i++)
      confetti.push({
        x: rnd(0, W),
        y: rnd(-H, H),
        vx: rnd(-0.3, 0.3),
        vy: rnd(0.6, 1.5),
        w: rnd(4, 7),
        h: rnd(6, 10),
        rot: rnd(0, 6.28),
        vr: rnd(-0.12, 0.12),
        c: pick(),
      });
  };
  const launch = () => rockets.push({ x: rnd(W * 0.2, W * 0.8), y: H, vy: rnd(-3.4, -2.6), ty: rnd(H * 0.16, H * 0.5), c: pick() });
  const explode = (x, y, c) => {
    for (let i = 0; i < 22; i++) {
      const a = (6.283 * i) / 22 + rnd(-0.1, 0.1);
      const sp = rnd(1, 2.6);
      sparks.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, c: Math.random() < 0.3 ? "#ffffff" : c });
    }
    flashes.push({ x, y, r: 2, a: 0.9 });
  };
  const frame = (t) => {
    if (!run) return;
    ctx.clearRect(0, 0, W, H);
    confetti.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      if (p.y > H + 12) {
        p.y = -12;
        p.x = rnd(0, W);
      }
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.c;
      ctx.globalAlpha = 0.95;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });
    ctx.globalAlpha = 1;
    if (t >= nextLaunch) {
      launch();
      nextLaunch = t + rnd(600, 1150);
    }
    for (let i = rockets.length - 1; i >= 0; i--) {
      const rk = rockets[i];
      rk.y += rk.vy;
      rk.vy += 0.03;
      ctx.fillStyle = rk.c;
      ctx.beginPath();
      ctx.arc(rk.x, rk.y, 1.8, 0, 6.283);
      ctx.fill();
      if (rk.y <= rk.ty || rk.vy >= 0) {
        explode(rk.x, rk.y, rk.c);
        rockets.splice(i, 1);
      }
    }
    for (let i = flashes.length - 1; i >= 0; i--) {
      const fl = flashes[i];
      fl.r += 2.4;
      fl.a -= 0.06;
      if (fl.a <= 0) {
        flashes.splice(i, 1);
        continue;
      }
      const g = ctx.createRadialGradient(fl.x, fl.y, 0, fl.x, fl.y, fl.r);
      g.addColorStop(0, "rgba(255,255,255," + fl.a + ")");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(fl.x, fl.y, fl.r, 0, 6.283);
      ctx.fill();
    }
    for (let i = sparks.length - 1; i >= 0; i--) {
      const sk = sparks[i];
      sk.x += sk.vx;
      sk.y += sk.vy;
      sk.vy += 0.03;
      sk.vx *= 0.985;
      sk.vy *= 0.985;
      sk.life -= 0.016;
      if (sk.life <= 0) {
        sparks.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = sk.life;
      ctx.fillStyle = sk.c;
      ctx.beginPath();
      ctx.arc(sk.x, sk.y, 1.6, 0, 6.283);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  };

  resize();
  seedConfetti();
  if (reduceFx) {
    ctx.clearRect(0, 0, W, H);
    confetti.forEach((p) => {
      ctx.save();
      ctx.translate(p.x, Math.abs(p.y) % H);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.c;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });
    return () => {};
  }
  raf = requestAnimationFrame(frame);
  window.addEventListener("resize", resize);
  return () => {
    run = false;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", resize);
  };
};

let welcomeShown = false;

export const WelcomePopup = () => {
  const { showWelcomepopup, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);
  const wordRowRef = useRef(null);
  const goBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!showWelcomepopup || welcomeShown) return;
    welcomeShown = true;
    setOpen(true);
    axiosInstance.post("Authentication/WelcomePopupShown").catch((e) => console.error(e));
  }, [showWelcomepopup, setUser]);

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    const stopFx = startFireworks(canvasRef.current);
    renderWord(wordRowRef.current, WORDS[0]);
    goBtnRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onResize = () => renderWord(wordRowRef.current, WORDS[0]);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      stopFx();
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      lastFocusedRef.current?.focus?.();
      setUser((prev) => (prev ? { ...prev, showWelcomepopup: false } : prev));
    };
  }, [open, setUser]);

  const close = () => setOpen(false);

  if (!open) return null;

  return (
    <div
      className="np-overlay is-open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="npTitle"
      aria-describedby="npBody"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className="np-modal">
        <div className="np-stage" aria-hidden="true" />
        <canvas ref={canvasRef} className="np-fx" aria-hidden="true" />

        <div className="np-parade" aria-hidden="true">
          <div className="wordrow" ref={wordRowRef} />
          <div className="np-heart h-right" aria-hidden="true">
            <span className="np-heart-pop">
              <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 29S3 20.6 3 12A7 7 0 0 1 16 8 7 7 0 0 1 29 12c0 8.6-13 17-13 17Z" fill="#e8203f" />
              </svg>
            </span>
          </div>
          <div className="np-heart h-left" aria-hidden="true">
            <span className="np-heart-pop">
              <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 29S3 20.6 3 12A7 7 0 0 1 16 8 7 7 0 0 1 29 12c0 8.6-13 17-13 17Z" fill="#e8203f" />
              </svg>
            </span>
          </div>
        </div>

        <button type="button" className="np-close" aria-label="Close" onClick={close}>
          &times;
        </button>

        <div className="np-icon" />

        <h2 className="np-title" id="npTitle">
          Our new site is live!
        </h2>
        <p className="np-body" id="npBody">
          We hope you love it. A big <strong>thank you</strong> for all your feedback &mdash; so much of it went straight into
          building this new experience. <strong>Keep it coming</strong>: we're always looking for ways to make things even
          better.
        </p>

        <div className="np-actions">
          <button type="button" ref={goBtnRef} className="np-btn np-btn--primary" onClick={close}>
            Start exploring
          </button>
        </div>
      </div>
    </div>
  );
};
